import type { Booking, RecurrenceSettings } from '@/types';

/**
 * How an "all future bookings" edit of a recurring series has to be saved.
 *
 * - `details-only`: the dates, day portion, hours, resources and repeat options are as
 *   they were, so the existing occurrences are patched in place with the changed details.
 * - `schedule-changed`: the occurrences keep their pattern but move, resize or change
 *   resources, so each one is updated (and re-planned) on its own dates.
 * - `recurrence-changed`: the repeat options changed, so the rest of the series is
 *   regenerated from the new pattern.
 * - `recurrence-removed`: the repeat was cleared, so the rest of the series is deleted.
 */
export type SeriesEditKind =
  | 'details-only'
  | 'schedule-changed'
  | 'recurrence-changed'
  | 'recurrence-removed';

export const isSameCalendarDay = (a?: string, b?: string): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const dateA = new Date(a);
  const dateB = new Date(b);
  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return a === b;
  return dateA.getFullYear() === dateB.getFullYear()
    && dateA.getMonth() === dateB.getMonth()
    && dateA.getDate() === dateB.getDate();
};

const normaliseDays = (days?: string[]): string =>
  (days ?? []).map(d => d.toLowerCase()).sort().join(',');

// Compares the repeat settings currently persisted for a series against the ones
// submitted from the repeat dialog. A difference means the remaining occurrences no
// longer match the pattern and have to be regenerated.
export const hasRecurrenceOptionsChanged = (
  original: RecurrenceSettings | undefined,
  updated: RecurrenceSettings | undefined
): boolean => {
  if (!original && !updated) return false;
  if (!original || !updated) return true;
  if (original.pattern !== updated.pattern) return true;
  if ((original.interval ?? 1) !== (updated.interval ?? 1)) return true;
  if (original.endType !== updated.endType) return true;
  if (updated.endType === 'on-date' && !isSameCalendarDay(original.endDate, updated.endDate)) return true;
  if (updated.endType === 'after' && (original.occurrences ?? null) !== (updated.occurrences ?? null)) return true;
  if (updated.pattern === 'weekly' && normaliseDays(original.daysOfWeek) !== normaliseDays(updated.daysOfWeek)) return true;
  if (updated.pattern === 'monthly') {
    if ((original.monthlyRecurrenceType ?? 'day-of-month') !== (updated.monthlyRecurrenceType ?? 'day-of-month')) return true;
    if (original.dayOfMonth != null && updated.dayOfMonth != null && original.dayOfMonth !== updated.dayOfMonth) return true;
    if (original.relativeWeekday?.occurrence !== updated.relativeWeekday?.occurrence) return true;
    if ((original.relativeWeekday?.weekday ?? '').toLowerCase() !== (updated.relativeWeekday?.weekday ?? '').toLowerCase()) return true;
  }
  // The series start date is only submitted when the repeat dialog was applied, and it
  // only survives on the parent occurrence's recurrence JSON, so it is compared only
  // when both sides carry it.
  if (original.startDate && updated.startDate && !isSameCalendarDay(original.startDate, updated.startDate)) return true;
  return false;
};

/**
 * Fields that place an occurrence in time or on resources, or tie it to its series and
 * tenant. A details-only patch never carries them, so every occurrence keeps its own.
 */
const NON_DETAIL_FIELDS: ReadonlySet<string> = new Set<keyof Booking>([
  'id',
  'startTime',
  'endTime',
  'durationType',
  'halfDayPeriod',
  'durationHours',
  'durationDays',
  'resourceId',
  'resourceIds',
  'resourceAssignments',
  'recurrence',
  'recurrenceGroupId',
  'isRecurrenceParent',
  'parentBookingId',
  'recurrencePending',
  'demandPlanGroupId',
  'demandId',
  'demandLineId',
  'source',
  'autoAdjustGroupId',
  'autoAdjustGroupStartTime',
  'autoAdjustGroupEndTime',
  'autoAdjustGroupPieceCount',
  'companyId',
  'auditHistory',
  'isFiltered',
]);

/**
 * The detail fields the batch-update endpoint applies (see `toBatchUpdateItem`). A
 * details-only patch made of these alone can be saved on the edited occurrence through
 * the same request as the ones following it.
 */
export const BATCH_UPDATE_DETAIL_FIELDS: ReadonlySet<string> = new Set<keyof Booking>([
  'status',
  'priority',
  'color',
  'projectName',
  'description',
  'billingClassification',
  'workType',
  'taskDescription',
  'bookingNotes',
  'bookingReference',
  'costCentreTask',
  'customer',
  'location',
  'addressLine2',
  'city',
  'county',
  'postcode',
  'country',
  'phone',
  'email',
  'timezone',
  'notifyReminders',
  'details',
  'requiredRole',
  'requiredSkills',
  'bookingTypeId',
  'projectId',
  'clientId',
  'costCentreId',
  'userChar',
  'userNumber',
  'userFlag',
  'userDate',
  'userNotes',
  'userTime',
]);

const isEmptyValue = (value: unknown): boolean =>
  value === undefined
  || value === null
  || value === ''
  || (Array.isArray(value) && value.length === 0)
  || (typeof value === 'object' && !(value instanceof Date) && Object.keys(value as object).length === 0);

const stableStringify = (value: unknown): string => {
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`;
  }
  return String(value);
};

/**
 * Whether two field values are the same as far as a save is concerned: ids compare as
 * strings (the form and the API disagree on number vs string), and every flavour of
 * "nothing" (undefined, null, '', [], {}) is the same nothing.
 */
export const isSameFieldValue = (a: unknown, b: unknown): boolean => {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  if (isEmptyValue(a) || isEmptyValue(b)) return false;
  return stableStringify(a) === stableStringify(b);
};

/** Stored and submitted times differ by the precision the database keeps. */
const TIME_TOLERANCE_MS = 1000;

const isSameInstant = (a: string | undefined, b: string | undefined): boolean => {
  if (!a || !b) return !a && !b;
  const timeA = new Date(a).getTime();
  const timeB = new Date(b).getTime();
  if (isNaN(timeA) || isNaN(timeB)) return a === b;
  return Math.abs(timeA - timeB) < TIME_TOLERANCE_MS;
};

const resourceSet = (booking: Partial<Booking>): string[] => {
  const ids = new Set((booking.resourceIds ?? []).map(String));
  if (booking.resourceId != null && booking.resourceId !== '') ids.add(String(booking.resourceId));
  return [...ids].sort();
};

type ResourceAssignments = NonNullable<Booking['resourceAssignments']>;

const assignmentOf = (assignments: ResourceAssignments | undefined, resourceId: string) => {
  const assignment = assignments?.[resourceId];
  return {
    requiredRole: assignment?.requiredRole ?? '',
    requiredSkills: [...(assignment?.requiredSkills ?? [])].sort(),
  };
};

const haveAssignmentsChanged = (original: Partial<Booking>, submitted: Partial<Booking>): boolean => {
  const submittedAssignments = submitted.resourceAssignments;
  if (!submittedAssignments) return false;
  return Object.keys(submittedAssignments).some(resourceId =>
    !isSameFieldValue(
      assignmentOf(original.resourceAssignments, resourceId),
      assignmentOf(submittedAssignments, resourceId),
    ));
};

/**
 * Whether the submitted form moves, resizes or re-resources the occurrence it was opened
 * on: its start or end, its day portion (full day, first or second half, custom hours) or
 * the resources it is booked on and what each is booked for. A field the form left out
 * keeps the occurrence's own value.
 */
export const hasScheduleChanged = (original: Booking, submitted: Partial<Booking>): boolean => {
  if (submitted.startTime !== undefined && !isSameInstant(original.startTime, submitted.startTime)) return true;
  if (submitted.endTime !== undefined && !isSameInstant(original.endTime, submitted.endTime)) return true;
  if (submitted.durationType !== undefined && submitted.durationType !== original.durationType) return true;
  if ('halfDayPeriod' in submitted && !isSameFieldValue(original.halfDayPeriod, submitted.halfDayPeriod)) return true;
  if (('resourceId' in submitted || 'resourceIds' in submitted)
    && resourceSet(original).join(',') !== resourceSet({ ...original, ...pickResources(submitted) }).join(',')) {
    return true;
  }
  return haveAssignmentsChanged(original, submitted);
};

const pickResources = (submitted: Partial<Booking>): Partial<Booking> => ({
  ...('resourceId' in submitted ? { resourceId: submitted.resourceId } : {}),
  ...('resourceIds' in submitted ? { resourceIds: submitted.resourceIds } : {}),
});

/** How an "all future bookings" save of `submitted` over `original` has to be made. */
export const classifySeriesEdit = (
  original: Booking,
  submitted: Partial<Booking>,
  seriesRecurrence: RecurrenceSettings | undefined,
): SeriesEditKind => {
  if (!submitted.recurrence || submitted.recurrence.pattern === 'none') return 'recurrence-removed';
  if (hasRecurrenceOptionsChanged(seriesRecurrence, submitted.recurrence)) return 'recurrence-changed';
  if (hasScheduleChanged(original, submitted)) return 'schedule-changed';
  return 'details-only';
};

/**
 * The detail fields the form changed from the occurrence it was opened on, as a patch
 * for that occurrence and every one following it. Schedule, resource, series and tenancy
 * fields are never part of it, so each occurrence keeps its own dates and resources.
 */
export const seriesDetailChanges = (original: Booking, submitted: Partial<Booking>): Partial<Booking> => {
  const changes: Record<string, unknown> = {};
  const originalFields = original as unknown as Record<string, unknown>;
  for (const [field, value] of Object.entries(submitted)) {
    if (NON_DETAIL_FIELDS.has(field)) continue;
    if (!isSameFieldValue(originalFields[field], value)) changes[field] = value;
  }
  return changes as Partial<Booking>;
};

/**
 * Whether the batch-update endpoint can apply every change in the patch. It merges only
 * the values a patch sets, so a field the edit cleared cannot travel through it.
 */
export const canApplyAsBatchPatch = (changes: Partial<Booking>): boolean =>
  Object.entries(changes).every(([field, value]) =>
    BATCH_UPDATE_DETAIL_FIELDS.has(field) && value !== undefined && value !== null);
